import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export type ATSStrengthLevel = "normal" | "strong";

type ATSStrengthContextType = {
  atsStrength: number;
  loading: boolean;
  error: string | null;
  calculateATSStrength: (resumeData: unknown) => Promise<number>;
};

const ATSStrengthContext = createContext<ATSStrengthContextType | undefined>(undefined);

export const ATSStrengthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [atsStrength, setATSStrength] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const calculateATSStrength = useCallback(async (resumeData: unknown): Promise<number> => {
    try {
      setLoading(true);
      setError(null);

      // Mock calculation - replace with actual ATS analysis
      const strength = Math.floor(Math.random() * 40) + 60; // 60-100%
      setATSStrength(strength);
      return strength;
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "ATS calculation failed";
      setError(errorMessage);
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      atsStrength,
      loading,
      error,
      calculateATSStrength,
    }),
    [atsStrength, loading, error, calculateATSStrength],
  );

  return <ATSStrengthContext.Provider value={value}>{children}</ATSStrengthContext.Provider>;
};

export function useATSStrength() {
  const context = useContext(ATSStrengthContext);
  if (context === undefined) {
    throw new Error("useATSStrength must be used within an ATSStrengthProvider");
  }
  return context;
}
